import { LinearGradient } from 'expo-linear-gradient';
import { ArrowRight, BarChart3, Mic, Plus, Settings, Zap } from 'lucide-react-native';
import type { ReactNode } from 'react';
import { useState } from 'react';
import { Platform, type ViewStyle } from 'react-native';
import { AnimatedCard, FadeIn } from '../../../components/animations';
import { Box, HStack, Text, VStack } from '../../../components/primitives';
import { BentoSurface } from '../../../components/primitives/BentoSurface';
import { Button } from '../../../components/primitives/Button';
import { useTranslation } from '../../../contexts/TranslationContext';
import { colors } from '../../../theme/colors';
import { shadows } from '../../../theme/shadows';
import { cardSurface } from '../../../theme/surfaces';
import { tokens } from '../../../theme/tokens';

interface QuickAction {
  id: string;
  label: string;
  description: string;
  icon: ReactNode;
  onPress: () => void;
  variant?: 'primary' | 'secondary';
}

interface QuickActionsProps {
  actions?: QuickAction[];
  onAddLead?: () => void;
  onViewAnalytics?: () => void;
  onRunWorkflow?: () => void;
  onVoiceCampaign?: () => void;
  onSettings?: () => void;
}

const NOOP = () => undefined;

const getDefaultActions = (props: QuickActionsProps, t: (key: string) => string): QuickAction[] => [
  {
    id: 'add-lead',
    label: t('dashboard.quickActions.newLead.label'),
    description: t('dashboard.quickActions.newLead.description'),
    icon: <Plus size={22} color={colors.background.primary} strokeWidth={2.4} />,
    onPress: props.onAddLead || NOOP,
    variant: 'primary',
  },
  {
    id: 'analytics',
    label: t('dashboard.quickActions.analytics.label'),
    description: t('dashboard.quickActions.analytics.description'),
    icon: <BarChart3 size={18} color={colors.primary[400]} strokeWidth={2} />,
    onPress: props.onViewAnalytics || NOOP,
    variant: 'secondary',
  },
  {
    id: 'workflow',
    label: t('dashboard.quickActions.runWorkflow.label'),
    description: t('dashboard.quickActions.runWorkflow.description'),
    icon: <Zap size={18} color={colors.primary[400]} strokeWidth={2} />,
    onPress: props.onRunWorkflow || NOOP,
    variant: 'secondary',
  },
  ...(props.onVoiceCampaign
    ? [
        {
          id: 'voice-campaign',
          label: t('dashboard.quickActions.voiceCampaign.label'),
          description: t('dashboard.quickActions.voiceCampaign.description'),
          icon: <Mic size={18} color={colors.primary[400]} strokeWidth={2} />,
          onPress: props.onVoiceCampaign,
          variant: 'secondary' as const,
        },
      ]
    : []),
];

function PrimaryActionCard({ action, index }: Readonly<{ action: QuickAction; index: number }>) {
  const [isHovered, setIsHovered] = useState(false);

  const webHoverStyle = Platform.select({
    web: {
      transition: 'transform 0.25s ease, box-shadow 0.25s ease',
      cursor: 'pointer',
      ...(isHovered && {
        transform: [{ translateY: -3 }],
        boxShadow: `0 18px 34px rgba(0, 0, 0, 0.34), 0 0 30px ${colors.glow.cyan}`,
      }),
    } as ViewStyle,
    default: {},
  });

  const hoverProps = Platform.select({
    web: {
      onMouseEnter: () => setIsHovered(true),
      onMouseLeave: () => setIsHovered(false),
    },
    default: {},
  });

  return (
    <FadeIn delay={260 + index * 90} translateY={10}>
      <AnimatedCard onPress={action.onPress} enablePress>
        <Box
          borderRadius="2xl"
          overflow="hidden"
          style={[shadows.glowHaloSoft, webHoverStyle]}
          {...hoverProps}
        >
          <LinearGradient
            colors={[colors.primary[500], colors.accent.blue[500], colors.metrics.campaigns]}
            locations={[0, 0.55, 1]}
            start={{ x: 0, y: 0.5 }}
            end={{ x: 1, y: 0.5 }}
            style={{
              padding: tokens.spacing.xl,
              borderRadius: tokens.radius.xl,
            }}
          >
            <HStack spacing="lg" align="center">
              <Box
                backgroundColor={colors.surface.row}
                borderRadius="lg"
                padding="md"
                alignItems="center"
                justifyContent="center"
              >
                {action.icon}
              </Box>
              <VStack spacing="xxs" style={{ flex: 1 }}>
                <Text
                  variant="h5"
                  color={colors.background.primary}
                  style={{ fontWeight: '800', letterSpacing: -0.4 }}
                >
                  {action.label}
                </Text>
                <Text variant="bodySmall" color={colors.text.inverseSubtle}>
                  {action.description}
                </Text>
              </VStack>
              <ArrowRight size={20} color={colors.background.primary} strokeWidth={2.4} />
            </HStack>
          </LinearGradient>
        </Box>
      </AnimatedCard>
    </FadeIn>
  );
}

function SecondaryActionCard({ action, index }: Readonly<{ action: QuickAction; index: number }>) {
  const [isHovered, setIsHovered] = useState(false);

  const webHoverStyle = Platform.select({
    web: {
      transition: 'background-color 0.2s ease, border-color 0.2s ease, transform 0.2s ease',
      cursor: 'pointer',
      ...(isHovered && {
        backgroundColor: colors.surface.rowHover,
        borderColor: colors.border.default,
        transform: [{ translateY: -2 }],
      }),
    } as ViewStyle,
    default: {},
  });

  const hoverProps = Platform.select({
    web: {
      onMouseEnter: () => setIsHovered(true),
      onMouseLeave: () => setIsHovered(false),
    },
    default: {},
  });

  return (
    <FadeIn delay={320 + index * 90} translateY={10} style={{ flexGrow: 1 }}>
      <AnimatedCard onPress={action.onPress} enablePress style={{ flexGrow: 1 }}>
        <Box
          backgroundColor={colors.surface.row}
          borderRadius="md"
          borderWidth={1}
          borderColor={isHovered ? colors.border.default : colors.border.muted}
          padding="md"
          style={[webHoverStyle, { flexGrow: 1 }]}
          {...hoverProps}
        >
          <VStack spacing="sm">
            {action.icon}
            <VStack spacing="xxs">
              <Text
                variant="bodySmall"
                color={colors.text.primary}
                style={{ fontWeight: '600' }}
                numberOfLines={1}
              >
                {action.label}
              </Text>
              <Text variant="caption" color={colors.text.tertiary}>
                {action.description}
              </Text>
            </VStack>
          </VStack>
        </Box>
      </AnimatedCard>
    </FadeIn>
  );
}

export function QuickActions({
  actions,
  onAddLead,
  onViewAnalytics,
  onRunWorkflow,
  onVoiceCampaign,
  onSettings,
}: Readonly<QuickActionsProps>) {
  const { t } = useTranslation();
  const props: QuickActionsProps = {
    actions,
    onAddLead,
    onViewAnalytics,
    onRunWorkflow,
    onVoiceCampaign,
    onSettings,
  };

  const displayActions = actions || getDefaultActions(props, t);
  const primaryAction = displayActions.find((a) => a.variant === 'primary');
  const secondaryActions = displayActions.filter((a) => a.variant !== 'primary');

  return (
    <AnimatedCard delay={300}>
      <Box
        testID="dashboard_quick_actions_container"
        padding="lg"
        overflow="hidden"
        style={[cardSurface(), { borderRadius: tokens.radius['2xl'], flex: 1, minHeight: 350 }]}
      >
        <BentoSurface radius={tokens.radius['2xl']} />
        <VStack spacing="lg">
          <HStack justify="between" align="center">
            <Text variant="h6" color={colors.text.primary} style={{ fontWeight: '700' }}>
              {t('dashboard.quickActions.title')}
            </Text>
            {props.onSettings && (
              <Button variant="ghost" size="sm" onPress={props.onSettings} style={{ padding: 8 }}>
                <Settings size={18} color={colors.text.secondary} strokeWidth={2} />
              </Button>
            )}
          </HStack>

          <VStack spacing="md">
            {primaryAction && <PrimaryActionCard action={primaryAction} index={0} />}

            <Box flexDirection="row" gap="sm">
              {secondaryActions.map((action, index) => (
                <Box key={action.id} style={{ flex: 1 }}>
                  <SecondaryActionCard action={action} index={index + 1} />
                </Box>
              ))}
            </Box>
          </VStack>
        </VStack>
      </Box>
    </AnimatedCard>
  );
}
